import { useCallback } from 'react';
import { useShowNotification } from '@core/js/hooks/useShowNotification';
import { useTranslations } from '@core/js/hooks/i18n';
import { useGameUrlFragment } from '../../../../../../../common/Game/hooks';
import { getCleanUrl } from '@core/js/utils/url.util';
import { useAppSelector } from '@core/js/store/hooks';
import { useBrowserShareAPI } from '@theme/js/hooks/swiss/useBrowserShareAPI';
import { prop } from 'ramda';

type UseShareGameButtonProps = {
    code: string;
    name: string;
    errorSharingMessage: string;
    notSupportedFeatureMessage: string;
}

export const useShareGameButton = ({ code, name, errorSharingMessage, notSupportedFeatureMessage }: UseShareGameButtonProps) => {
    const showNotification = useShowNotification();
    const gameUrlFragment = useGameUrlFragment(code);
    const dynamicTags = useAppSelector(prop('dynamicTags'));
    const gameLinkTag = dynamicTags.DTAG_GAMEINFO_POPUP_LINK || '/online-game';
    const location = window.location.href;
    const cleanUrl = getCleanUrl();
    const getIndexGameUrl = location.indexOf(cleanUrl);
    const newGameUrl = `${location.slice(0, getIndexGameUrl)}${gameLinkTag}/${gameUrlFragment}`;
    const gameUrl = location.includes(gameUrlFragment) ? location : newGameUrl;
    const t = useTranslations();
    const title = t('playtech.mobile.system.responsivegames.game.infoPopup.shareTitle', [name]);
    const text = t('playtech.mobile.system.responsivegames.game.infoPopup.shareText', [name]);

    const onError = useCallback(() =>
        showNotification(
            errorSharingMessage,
            {
                variant: 'error',
                anchorOrigin: { vertical: 'top', horizontal: 'right' }
            }),
            [errorSharingMessage, showNotification]
        );

    const { shareCallback } = useBrowserShareAPI({
        title,
        url: gameUrl,
        text,
        notSupportedFeatureMessage,
        onError
    });

    return shareCallback;
};
